import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  X,
  CaretRight,
  Camera,
  Sparkle,
  Keyboard,
  CircleNotch,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../../../lib/api";
import { usePaywall } from "../../../lib/paywall";
import { resizeToDataUrl } from "../../../lib/imageUtils";
import { TextField, SelectField } from "./FormPrimitives";

/* ---------- Method picker — same visual as PhotoIdMethodPicker ---------- */
export function InsuranceMethodPicker({ onPick, onCancel }) {
  const methods = [
    {
      id: "scan",
      Icon: Sparkle,
      title: "Scan Policy Doc with camera",
      sub: "Use AI to auto-fill the fields",
      color: "#E55B2C",
      soft: "#FDE6D8",
    },
    {
      id: "manual",
      Icon: Keyboard,
      title: "Enter manually",
      sub: "Type each field yourself",
      color: "#0E2C54",
      soft: "#EAF2FB",
    },
  ];
  return (
    <div
      className="mt-5 bg-white rounded-2xl shadow-soft border border-navy/10 p-4 space-y-3"
      data-testid="insurance-method-picker"
    >
      <div className="flex items-center justify-between mb-1">
        <div className="font-jakarta font-extrabold text-navy text-base">
          How would you like to add this policy?
        </div>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancel"
          className="w-9 h-9 rounded-full bg-bone/70 flex items-center justify-center"
        >
          <X size={18} weight="bold" color="#0E2C54" />
        </button>
      </div>
      {methods.map((m) => (
        <button
          key={m.id}
          type="button"
          data-testid={`insurance-method-${m.id}`}
          onClick={() => onPick(m.id)}
          className="w-full bg-bone/40 hover:bg-bone/70 border border-navy/5 rounded-2xl p-3 flex items-center gap-3 text-left"
        >
          <div
            className="shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center"
            style={{ backgroundColor: m.soft }}
          >
            <m.Icon size={22} weight="duotone" color={m.color} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-jakarta font-extrabold text-navy text-base leading-tight">
              {m.title}
            </div>
            <div className="text-navy-soft text-sm font-jakarta font-medium mt-0.5">
              {m.sub}
            </div>
          </div>
          <CaretRight size={16} weight="bold" color="#4A5D78" />
        </button>
      ))}
    </div>
  );
}

/* ---------- The form ---------- */
/**
 * Travel-insurance add form. `mode === "scan"` auto-opens the camera on
 * mount, ships the photo to /api/vault/scan-insurance for OCR, and
 * pre-fills the editable fields. `mode === "manual"` skips the scan and
 * just renders the empty form.
 */
export default function InsuranceForm({
  mode,
  familyMembers = [],
  onSave,
  onCancel,
}) {
  const { handle402 } = usePaywall();
  const [draft, setDraft] = useState({
    holder: "self",
    provider: "",
    policy_no: "",
    plan_type: "",
    coverage_start: "",
    coverage_end: "",
    emergency_phone: "",
  });
  const [scanning, setScanning] = useState(false);
  const scanRef = useRef(null);

  // Auto-trigger camera on mount in scan mode.
  const triggeredRef = useRef(false);
  useEffect(() => {
    if (triggeredRef.current) return;
    triggeredRef.current = true;
    if (mode === "scan") setTimeout(() => scanRef.current?.click(), 200);
  }, [mode]);

  const holderOptions = useMemo(
    () => [
      { id: "self", label: "Me" },
      ...familyMembers.map((m) => ({
        id: m.id,
        label: m.name || "Family member",
      })),
    ],
    [familyMembers]
  );

  async function onScanFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Please upload an image");
      return;
    }
    const dataUrl = await resizeToDataUrl(file, 1600, 0.82);
    setScanning(true);
    try {
      const b64 = dataUrl.split(",", 2)[1];
      const r = await api.post(`/vault/scan-insurance`, {
        image_base64: b64,
      });
      const f = r.data?.fields || {};
      setDraft((d) => ({
        ...d,
        provider: f.provider || d.provider,
        policy_no: f.policy_no || d.policy_no,
        plan_type: f.plan_type || d.plan_type,
        coverage_start: f.coverage_start || d.coverage_start,
        coverage_end: f.coverage_end || d.coverage_end,
        emergency_phone: f.emergency_phone || d.emergency_phone,
      }));
      const filledCount = Object.values(f).filter(Boolean).length;
      if (filledCount === 0) {
        toast.message(
          "Couldn't read this policy — try a brighter, sharper photo or fill manually"
        );
      } else {
        toast.success(
          `Auto-filled ${filledCount} fields — review before saving`
        );
      }
    } catch (err) {
      if (!handle402(err)) {
        toast.error(err?.response?.data?.detail || "Scan failed");
      }
    } finally {
      setScanning(false);
    }
  }

  function submit(e) {
    e?.preventDefault?.();
    const hasField = [
      "provider",
      "policy_no",
      "plan_type",
      "coverage_start",
      "coverage_end",
      "emergency_phone",
    ].some((k) => draft[k]?.trim());
    if (!hasField) {
      toast.error("Fill at least one field");
      return;
    }
    onSave(draft);
  }

  return (
    <form
      onSubmit={submit}
      data-testid="insurance-form"
      className="mt-5 bg-white rounded-2xl shadow-soft border border-navy/10 p-4 space-y-3"
    >
      <div className="flex items-center justify-between mb-1">
        <div className="font-jakarta font-extrabold text-navy text-base">
          {mode === "scan" ? "Scan policy" : "New travel insurance"}
        </div>
        <button
          type="button"
          onClick={onCancel}
          aria-label="Cancel"
          className="w-9 h-9 rounded-full bg-bone/70 flex items-center justify-center"
        >
          <X size={18} weight="bold" color="#0E2C54" />
        </button>
      </div>

      {/* Scan re-trigger button — only shown in scan mode, lets the user
          retake a blurry photo without backing out of the form. */}
      {mode === "scan" && (
        <button
          type="button"
          onClick={() => scanRef.current?.click()}
          data-testid="insurance-rescan-btn"
          disabled={scanning}
          className="w-full h-20 rounded-xl border-2 border-dashed border-navy/20 bg-bone/40 flex items-center justify-center gap-2 text-navy-soft font-jakarta font-medium disabled:opacity-60"
        >
          {scanning ? (
            <>
              <CircleNotch size={20} weight="bold" className="animate-spin text-orange" />
              Reading your policy…
            </>
          ) : (
            <>
              <Camera size={22} weight="duotone" color="#0E2C54" />
              <span className="text-sm">Tap to rescan policy doc</span>
            </>
          )}
        </button>
      )}
      <input
        ref={scanRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onScanFile}
        data-testid="insurance-scan-input"
      />

      <SelectField
        label="Insured"
        testId="vault-field-holder"
        value={draft.holder}
        options={holderOptions}
        onChange={(v) => setDraft((d) => ({ ...d, holder: v }))}
        hint={
          familyMembers.length === 0
            ? "Tip: add Family members to assign a policy to them."
            : null
        }
      />
      <TextField
        label="Insurance provider"
        placeholder="Allianz Travel"
        testId="vault-field-provider"
        value={draft.provider}
        onChange={(v) => setDraft((d) => ({ ...d, provider: v }))}
      />
      <TextField
        label="Policy #"
        placeholder="AT-3398-22K"
        testId="vault-field-policy_no"
        value={draft.policy_no}
        onChange={(v) => setDraft((d) => ({ ...d, policy_no: v }))}
      />
      <TextField
        label="Plan type"
        placeholder="Single trip · Medical + cancellation"
        testId="vault-field-plan_type"
        value={draft.plan_type}
        onChange={(v) => setDraft((d) => ({ ...d, plan_type: v }))}
      />
      <TextField
        label="Coverage starts"
        placeholder="Aug 14 2026"
        testId="vault-field-coverage_start"
        value={draft.coverage_start}
        onChange={(v) => setDraft((d) => ({ ...d, coverage_start: v }))}
      />
      <TextField
        label="Coverage ends"
        placeholder="Aug 21 2026"
        testId="vault-field-coverage_end"
        value={draft.coverage_end}
        onChange={(v) => setDraft((d) => ({ ...d, coverage_end: v }))}
      />
      <TextField
        label="24/7 emergency #"
        placeholder="+1 800 555 0199"
        testId="vault-field-emergency_phone"
        value={draft.emergency_phone}
        onChange={(v) => setDraft((d) => ({ ...d, emergency_phone: v }))}
      />

      <button
        type="submit"
        data-testid="vault-save-btn"
        disabled={scanning}
        className="w-full bg-orange text-white font-jakarta font-extrabold text-base rounded-full py-3 shadow-soft disabled:opacity-60"
      >
        Save to Vault
      </button>
    </form>
  );
}
